import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin, Phone, Sparkles, ArrowRight, CheckCircle2, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Logo from '@/components/Logo';
import { useToast } from '@/components/ui/use-toast';
import { restaurantApi } from '@/services/api/restaurantApi';
import { useAuth } from '@/services/auth/authContext';
import { getApiUrl } from '@/config/api';
import Cookies from 'js-cookie';

const LocationSetup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, refreshUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingData, setExistingData] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [locationLimit, setLocationLimit] = useState(1);
  const [planName, setPlanName] = useState('');
  
  const [formData, setFormData] = useState({
    restaurantName: '',
    cuisineType: '',
    locationNickname: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: ''
  });

  // Cuisine types
  const cuisineTypes = [
    'American', 'Italian', 'Mexican', 'Chinese', 'Japanese',
    'Indian', 'Thai', 'French', 'Mediterranean', 'Korean',
    'Vietnamese', 'Greek', 'Spanish', 'Middle Eastern', 'Other'
  ];

  // US States
  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  // Fetch existing restaurant data and locations
  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        // Get restaurant profile
        const profileResponse = await fetch(getApiUrl('restaurants/profile'), {
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token') || ''}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (profileResponse.ok) {
          const profile = await profileResponse.json();
          setExistingData(profile);
          
          // Pre-populate form with chain data
          setFormData(prev => ({
            ...prev,
            restaurantName: profile.restaurant_name || '',
            cuisineType: profile.cuisine_type || ''
          }));
        }

        // Get locations
        const locationsResponse = await fetch(getApiUrl('restaurants/locations'), {
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token') || ''}`,
            'Content-Type': 'application/json'
          }
        });

        if (locationsResponse.ok) {
          const locData = await locationsResponse.json();
          setLocations(locData.locations || []);
        }

        // Get location availability to check limits
        const availabilityResponse = await fetch(getApiUrl('restaurants/location-availability'), {
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token') || ''}`,
            'Content-Type': 'application/json'
          }
        });

        if (availabilityResponse.ok) {
          const availability = await availabilityResponse.json();
          setLocationLimit(availability.location_limit || 1);
          setPlanName(availability.plan_name || '');
          
          // Check if user can add more locations
          if (availability.locations_used >= availability.location_limit) {
            toast({
              title: "Location Limit Reached",
              description: `Your ${availability.plan_name} plan allows ${availability.location_limit} locations. Please upgrade to add more.`,
              variant: "destructive"
            });
            navigate('/restaurant-settings');
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching existing data:', error);
        toast({
          title: "Error",
          description: "Failed to load restaurant information",
          variant: "destructive"
        });
      }
    };

    fetchExistingData();
  }, [navigate, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Format phone number as user types
    if (name === 'phoneNumber') {
      // Remove all non-digits
      const digits = value.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedDigits = digits.slice(0, 10);
      
      // Format as (XXX) XXX-XXXX
      let formatted = '';
      if (limitedDigits.length > 0) {
        if (limitedDigits.length <= 3) {
          formatted = `(${limitedDigits}`;
        } else if (limitedDigits.length <= 6) {
          formatted = `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
        } else {
          formatted = `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
        }
      }
      
      setFormData({
        ...formData,
        phoneNumber: formatted
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const validateForm = () => {
    if (!formData.restaurantName.trim()) {
      toast({ title: "Error", description: "Restaurant name is required", variant: "destructive" });
      return false;
    }
    if (!formData.cuisineType) {
      toast({ title: "Error", description: "Please select a cuisine type", variant: "destructive" });
      return false;
    }
    if (!formData.address.trim()) {
      toast({ title: "Error", description: "Address is required", variant: "destructive" });
      return false;
    }
    if (!formData.city.trim()) {
      toast({ title: "Error", description: "City is required", variant: "destructive" });
      return false;
    }
    if (!formData.state.trim()) {
      toast({ title: "Error", description: "State is required", variant: "destructive" });
      return false;
    }
    if (!formData.zipCode.trim()) {
      toast({ title: "Error", description: "ZIP code is required", variant: "destructive" });
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      toast({ title: "Error", description: "Phone number is required", variant: "destructive" });
      return false;
    }
    
    // Validate phone has 10 digits
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      toast({ title: "Error", description: "Please enter a valid 10-digit phone number", variant: "destructive" });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for API - send only digits for phone
      const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
      
      const locationData = {
        restaurant_name: formData.restaurantName,
        cuisine_type: formData.cuisineType,
        location_nickname: formData.locationNickname || null,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        phone_number: phoneDigits, // Send only digits
        // Don't send terms_accepted or privacy_accepted for location addition
      };
      
      console.log('📤 Submitting location data:', locationData);
      
      // Call the add-location specific endpoint
      const response = await fetch(getApiUrl('locations/add'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(locationData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add location');
      }
      
      const result = await response.json();
      
      console.log('🎉 Location added successfully:', result);
      console.log('📍 New location rest_id:', result.rest_id);
      
      // NO TOKEN REFRESH NEEDED!
      // Our menu upload is chain-aware and will work with ANY rest_id from the chain
      // This is much simpler and more reliable
      console.log('✅ No token refresh needed - menu upload is chain-aware');
      console.log('🔄 Menu upload will automatically work with the new location');
      
      toast({
        title: "Location Added! 🎉",
        description: `Location #${locations.length + 1} has been added successfully. Now let's upload the menu.`,
      });
      
      // Navigate to menu upload for the new location
      navigate('/restaurant-menu-upload?new_location=true');
      
    } catch (error: any) {
      console.error('Location addition error:', error);
      
      toast({
        title: "Error",
        description: error.message || "Failed to add location. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 px-8 py-6">
        <Logo />
      </div>

      {/* Main Content with horizontal layout */}
      <div className="relative z-10 max-w-7xl mx-auto px-8 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-xl"
        >
          <div className="flex gap-8">
            {/* Left side - Form (60%) */}
            <div className="flex-1 max-w-3xl">
              {/* Location counter */}
              <div className="flex items-center justify-start mb-6">
                <div className="bg-dineai-yellow text-black px-4 py-2 rounded-full font-semibold text-sm">
                  🏪 LOCATION {locations.length + 1} OF {locationLimit}
                </div>
              </div>

              {/* Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-white mb-2">
                  Congratulations on Expanding! 🎉
                </h1>
                <p className="text-white/80 text-lg">
                  Welcome back, {user?.name || existingData?.owner_name}! Let's set up your new location.
                </p>
              </div>

              {/* Existing Locations - Compact */}
              {locations.length > 0 && (
                <div className="mb-4 p-3 bg-white/5 rounded-xl">
                  <p className="text-white/60 text-sm mb-1">Your current locations:</p>
                  <div className="flex flex-wrap gap-2">
                    {locations.map((loc, idx) => (
                      <div key={loc.rest_id} className="flex items-center text-white/80 text-xs bg-white/10 px-2 py-1 rounded">
                        <CheckCircle2 className="h-3 w-3 mr-1 text-green-400" />
                        <span>#{idx + 1}: {loc.city}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Horizontal Form Layout */}
              <div className="space-y-4">
                {/* Row 1: Restaurant Name & Cuisine */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Restaurant Chain Name
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                      <Input
                        type="text"
                        name="restaurantName"
                        value={formData.restaurantName}
                        onChange={handleInputChange}
                        placeholder="Your restaurant name"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Cuisine Type
                    </label>
                    <Select value={formData.cuisineType} onValueChange={(value) => setFormData({...formData, cuisineType: value})}>
                      <SelectTrigger className="bg-white/10 border-white/20 text-white h-10">
                        <SelectValue placeholder="Select cuisine type" />
                      </SelectTrigger>
                      <SelectContent>
                        {cuisineTypes.map(cuisine => (
                          <SelectItem key={cuisine} value={cuisine.toLowerCase()}>
                            {cuisine}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Row 2: Location Nickname & Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Location Nickname <span className="text-white/50">(Optional)</span>
                    </label>
                    <div className="relative">
                      <Store className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                      <Input
                        type="text"
                        name="locationNickname"
                        value={formData.locationNickname}
                        onChange={handleInputChange}
                        placeholder="e.g., Downtown, Airport"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      Phone Number <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                      <Input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Row 3: Street Address */}
                <div>
                  <label className="block text-white text-sm font-medium mb-1">
                    Street Address <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                    <Input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      required
                    />
                  </div>
                </div>

                {/* Row 4: City, State, ZIP */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      City <span className="text-red-400">*</span>
                    </label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="San Francisco"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      State <span className="text-red-400">*</span>
                    </label>
                    <Select value={formData.state} onValueChange={(value) => setFormData({...formData, state: value})}>
                      <SelectTrigger className="bg-white/10 border-white/20 text-white h-10">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {usStates.map(state => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      ZIP Code <span className="text-red-400">*</span>
                    </label>
                    <Input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="34579"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      maxLength={5}
                      required
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/restaurant-settings')}
                    className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex-1 bg-dineai-yellow text-black hover:bg-yellow-400 font-semibold"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          <Sparkles className="h-5 w-5" />
                        </motion.div>
                        Adding Location...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Add Location
                        <ArrowRight className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right side - 3D Illustration (40%) */}
            <div className="hidden lg:flex items-center justify-center w-2/5 max-w-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="relative"
              >
                <img 
                  src="/restaurant-icon.png" 
                  alt="Restaurant Location" 
                  className="w-full h-auto object-contain drop-shadow-2xl"
                />
                <motion.div
                  animate={{ 
                    y: [0, -10, 0],
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2"
                >
                  <p className="text-white text-sm font-medium">
                    Growing your empire! 🚀
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="mt-6 text-center">
            <p className="text-white/60 text-sm">
              After adding this location, you'll be able to upload its menu
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LocationSetup;